import { Button } from '@nextui-org/react'
import { useSnackbar } from 'notistack'
import { FC, useCallback } from 'react'
import { MdFileDownload } from 'react-icons/md'
import * as XLSX from 'xlsx'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import LogiTable from '../LogiTable'
import Panel from '../Panel'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { labelsApi, EnvioDetalle, EnvioEquipoRow } from '../../api'
import { TERMINO_ETIQUETA_LOWER } from '../../constants'

const columns = [
  { name: 'Id Tradein', uid: 'id_tradein' },
  { name: 'Folio', uid: 'folio' },
  { name: 'Equipo', uid: 'equipo' },
  { name: 'Valor', uid: 'valor' },
  { name: 'Incluido en envío', uid: 'fecha_inclusion' },
  { name: 'Ejecutivo', uid: 'ejecutivo' }
]

const fmtDateTime = (iso: string | null): string => {
  if (iso === null) return '—'
  const d = new Date(iso)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} · ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const fmtMoney = (n: number | null): string => {
  if (n === null) return '—'
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 })
}

const EnvioDetalleView: FC = () => {
  const { selectedCollectionId, selectedBoxSeal, selectedBoxSeal2, selectedBoxKey, selectedBoxTracking } = useLogisticBox()
  const idSeller = useSelectedSeller()
  const { enqueueSnackbar } = useSnackbar()

  const fetcher = useCallback(async (): Promise<EnvioDetalle | null> => {
    if (selectedCollectionId === null) throw new Error(`Selecciona una ${TERMINO_ETIQUETA_LOWER} desde Recolecciones`)
    return await labelsApi.envioByCollection(selectedCollectionId, idSeller, selectedBoxSeal, selectedBoxSeal2, selectedBoxKey, selectedBoxTracking)
  }, [selectedCollectionId, selectedBoxSeal, selectedBoxSeal2, selectedBoxKey, selectedBoxTracking, idSeller])

  const detail = useApi<EnvioDetalle | null>(fetcher, [selectedCollectionId, selectedBoxSeal, selectedBoxSeal2, selectedBoxKey, selectedBoxTracking, idSeller])
  const d = detail.data

  const handleExport = (): void => {
    const rows = d?.equipos ?? []
    if (rows.length === 0) { enqueueSnackbar('No hay equipos para exportar', { variant: 'info' }); return }
    const data = rows.map(e => ({
      'Id Tradein': e.id_tradein,
      Folio: e.folio ?? '',
      Equipo: e.equipo,
      Valor: e.valor ?? '',
      'Incluido en envío': fmtDateTime(e.fecha_inclusion),
      Ejecutivo: e.ejecutivo ?? ''
    }))
    const now = new Date()
    const pad = (n: number): string => String(n).padStart(2, '0')
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`
    const ref = d?.tracking_number ?? `col${selectedCollectionId ?? ''}`
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Envío')
    XLSX.writeFile(wb, `Envio_${ref}_${stamp}.xlsx`)
    enqueueSnackbar(`Excel exportado · ${rows.length} equipos`, { variant: 'success' })
  }

  const renderCell = (e: EnvioEquipoRow, columnKey: string): JSX.Element => {
    switch (columnKey) {
      case 'id_tradein':
        return <span className='font-medium text-xs'>{e.id_tradein}</span>
      case 'folio':
        return <span className='text-xs'>{e.folio ?? '—'}</span>
      case 'equipo':
        return <span className='text-xs'>{e.equipo}</span>
      case 'valor':
        return <span className='text-xs'>{fmtMoney(e.valor)}</span>
      case 'fecha_inclusion':
        return <span className='text-xs'>{fmtDateTime(e.fecha_inclusion)}</span>
      case 'ejecutivo':
        return <span className='text-xs'>{e.ejecutivo ?? '—'}</span>
      default:
        return <span className='text-xs'>{(e as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      <BackButton to='etiquetas' />

      {/* <Panel>
        <ApiStateView loading={detail.loading} error={detail.error} onRetry={detail.reload}>
          <div className='flex items-start justify-between gap-6 flex-wrap'>
            <div>
              <div className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Detalle del envío</div>
              <h1 className='text-xl font-bold text-[#151930] mt-0.5'>{tienda}</h1>
              <p className='text-xs text-[#151930]/60 mt-1'>
                Tracking: <b className=''>{d?.tracking_number ?? '—'}</b>
                {d?.referencia !== null && d?.referencia !== undefined && (
                  <> · Referencia: <b>{d.referencia}</b></>
                )}
                {d?.fecha_gatillo !== null && d?.fecha_gatillo !== undefined && (
                  <> · gatillado {fmtDateTime(d.fecha_gatillo)}</>
                )}
              </p>
            </div>
            {d?.pdf_url !== null && d?.pdf_url !== undefined && (
              <Button as='a' href={d.pdf_url} target='_blank' rel='noreferrer' className='bg-[#151930] text-white text-xs font-semibold'>
                Ver {TERMINO_ETIQUETA_LOWER} PDF
              </Button>
            )}
          </div>
          <div className='mt-4 grid grid-cols-2 lg:grid-cols-3 gap-3'>
            <MiniKpi value={String(d?.total_equipos ?? 0)} label='Equipos en el envío' tone='lila' />
            <MiniKpi value={d?.estado ?? '—'} label='Estado del envío' tone='blue' />
            <MiniKpi value={`R${d?.regla_gatillo ?? '—'}`} label='Regla que lo gatilló' tone='beige' />
          </div>
        </ApiStateView>
      </Panel> */}

      <Panel>
        <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
          <h2 className='font-semibold text-[#151930]'>Equipos que lleva la {TERMINO_ETIQUETA_LOWER} · {d?.total_equipos ?? 0}</h2>
          <Button
            variant='bordered'
            className='border-gray-200 text-xs'
            startContent={<MdFileDownload className='text-base' />}
            isDisabled={detail.loading || (d?.equipos.length ?? 0) === 0}
            onPress={handleExport}
          >
            Exportar
          </Button>
        </div>

        <ApiStateView
          loading={detail.loading}
          error={detail.error}
          onRetry={detail.reload}
          empty={!detail.loading && (d?.equipos.length ?? 0) === 0}
          emptyText='Este envío no tiene equipos asociados'
        >
          <LogiTable<EnvioEquipoRow>
            columns={columns}
            items={d?.equipos ?? []}
            renderCell={renderCell}
            rowKey={(e) => e.id_tradein}
            ariaLabel='Equipos del envío'
            loading={detail.loading}
            emptyText='Este envío no tiene equipos asociados'
            rowsPerPage={10}
            bottomExtra={
              <div className='flex items-center justify-between text-xs text-[#151930]/60'>
                <span>Mostrando {d?.equipos.length ?? 0} equipos</span>
              </div>
            }
          />
        </ApiStateView>
      </Panel>
    </div>
  )
}

export default EnvioDetalleView
